<template>
  <div class="d-inline-block text-center">
    <v-dialog
      v-if="device"
      v-model="showState"
      max-width="80%"
    >
      <videon-card :heading="device.deviceName() + ' Streaming Status'" showClose="true" @close="showState = false">
        <v-card-text v-if="device.error" class="">
          <v-alert type="error">
            {{ device.errorText }}
          </v-alert>
        </v-card-text>
        
        <v-card-text v-if="device.state" class="px-5">
          <v-row>
            <videon-state-tile
              heading="Device Online"
              class="text-capitalize"
            >
              <span 
                :class="(!device.isOnline()) ? 'error--text' : ''"
              >
                {{ device.isOnline() }}
              </span>
            </videon-state-tile>
            <videon-state-tile
              heading="Last Boot"
            >
              {{ device.state.last_boot | moment("YYYY/MM/DD hh:mm:ss a") }}
            </videon-state-tile>
            <videon-state-tile
              heading="Device Uptime"
            >
              {{ uptime }}
            </videon-state-tile>
            <videon-state-tile
              heading="Last Update"
            >
              {{ device.state.last_state_update | moment("YYYY/MM/DD hh:mm:ss a") }}
            </videon-state-tile>
          </v-row>
          
          <div v-if="alerts.length > 0" class="ma-2">
            <v-row><h5 class="text--disabled">Device Alerts</h5></v-row>
            <v-row><v-divider /></v-row>
            <div>
              <v-row class="my-5">
                <ul class="ma-2">
                  <li 
                    v-for="alert in alerts" 
                    v-bind:key="alert.alert_guid" 
                    @click="$router.push('/devices/' + device.device_guid + '/config/Alerts'); showState = false"
                    style="cursor: pointer;"
                  >
                    <strong class="error--text">{{ alert.label }}</strong>
                    <v-chip v-if="alert.legacy" small outlined class="ml-1">
                      Legacy Alert
                    </v-chip>
                    <v-chip v-if="alert.silenced" small outlined class="ml-1">
                      Silenced
                    </v-chip>
                  </li>
                </ul>
              </v-row>
            </div>
          </div>
          
          <div class="ma-2">
            <v-row><h5 class="text--disabled">Configuration State</h5></v-row>
            <v-row><v-divider /></v-row>
            
            <device-monitor-view :device="device" class="mt-5" />
          </div>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    <v-chip :color="status.color" label outlined small @click="showState = true">
      <v-progress-circular :size="12" :width="1" v-if="loading" indeterminate color="grey"></v-progress-circular>
      <span v-else>
        {{ status.label }}
      </span>
    </v-chip>
  </div>
</template>

<script>
  import moment from 'moment'
  
  import DeviceMonitorView from '../../device/components/DeviceMonitorView.vue'
  
  export default {
    name: 'StreamingStatusLabel',
    
    components: {
      DeviceMonitorView
    },
    
    props: ['device', 'noSync'],
    
    computed: {
      loading() {
        if (this.device.isReady()) {
          var inputsShadow = this.$deviceShadows.getDeviceShadow(this.device.device_guid, 'Inputs') 
          var outputsShadow = this.$deviceShadows.getDeviceShadow(this.device.device_guid, 'Outputs') 
        
          if (this.device.error || (this.device.state && inputsShadow.ready && outputsShadow.ready)) {
            return false
          }
        }
        
        return true
      },
      alerts() {
        return this.$alerts.alertsForDevice(this.device.device_guid, true)
      },
      online() {
        return this.device.isOnline()
      },
      inputsState() {
        return this.device.inputsState()
      },
      outputsState() {
        return this.device.outputsState()
      },
      status() {
        if (!this.loading) {
          if (this.alerts.length > 0) {
            if (!this.device.isOnline()) {
              return {'label': 'Device Alerts', 'color': 'grey'}
            }
            return {'label': 'Device Alerts', 'color': 'red'}
          } else if (this.device.error) {
            return {'label': 'State Unknown', 'color': 'red'}
          } else if (!this.device.isOnline()) {
            return {'label': 'Device Offline', 'color': 'grey'}
          } else if (this.outputsState && this.outputsState.filter(output => this.$helpers.outputStatus(output).streaming === false && this.$helpers.outputStatus(output).enabled === true).length > 0) {
            return {'label': 'Error Streaming', 'color': 'red'}
          } else if (this.outputsState && this.outputsState.filter(output => this.$helpers.outputStatus(output).streaming === true).length > 0) {
            return {'label': 'Streaming', 'color': 'green'}
          } else if (this.outputsState && this.outputsState.filter(output => this.$helpers.outputStatus(output).streaming === false).length > 0) {
            return {'label': 'Not Streaming', 'color': 'orange'}
          } else if (this.outputsState) {
            return {'label': 'Not Configured', 'color': ''}
          }
        }
        return {'label': 'Unknown', 'color': 'grey'}
      },
      uptime() {
        if (!this.device.isOnline()) {
          return 'Offline'
        } else if (this.device && this.device.state) {
          if (this.device.state.last_boot && this.device.last_api_request_id) {
            return moment(this.device.state.last_boot).fromNow(true)
          }
        }
        return 'Unknown'
      },
    },
    mounted() {
      this.syncDocuments()
    },
    beforeDestroy() {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer)
        this.refreshTimer = false
      }
    },
    data() {
      return {
        inputPreviewSupportedVersion: this.$helpers.parseVersion('cloud', 'v1.3.0'),
        
        showState: false,
        
        refreshTimer: false,
        refreshInterval: this.$helpers.getRefreshInterval(20000, 30000)
      }
    },
    methods: {
      inputName(shadowDocument) {
        if (shadowDocument.config.name) {
          return this.$helpers.htmlDecode(shadowDocument.config.name)
        }
        
        return 'Input #' + shadowDocument.id
      },
      outputName(shadowDocument) {
        if (shadowDocument.config.name) {
          return this.$helpers.htmlDecode(shadowDocument.config.name)
        }
        
        var outputName = 'Output #' + shadowDocument.id
        if (shadowDocument.type) {
          outputName += ' (' + this.$helpers.getLabel(shadowDocument.type) + ')'
        }
        return outputName
      },
      isPreviewSupported(index = 0) {
        if (this.device.thumbnailEnabled(index) && this.$helpers.versionCheck(this.inputPreviewSupportedVersion, this.device.cloud_version)) {
          return true
        }
        return false
      },
      syncDocuments() {
        if (this.device.state && !this.noSync) {
          console.log('StreamingStatusLabel ' + this.device.device_guid + ' syncDocuments')
          this.device.syncDocuments()
        }
        
        if (this.refreshInterval) {
          this.refreshTimer = setTimeout(() => {
            this.syncDocuments()
          }, this.refreshInterval)
        }
      }
    }
  }
</script>